import React, { createContext, useContext, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import {
  FlowTelemetryResponse,
  NH3TelemetryResponse,
  BarnTemperatureTelemetryResponse,
} from '../types/telemetry';
import { FlowThreshold } from '../hooks/useFlowThresholds';
import { API_BASE_URL } from '../config/api';

interface TelemetryContextType {
  // Sub-domain data
  flowTelemetry: FlowTelemetryResponse[] | null;
  nh3Telemetry: NH3TelemetryResponse[] | null;
  barnTemperature: BarnTemperatureTelemetryResponse[] | null;
  flowThresholds: FlowThreshold[] | null;

  // Aggregate loading/error state
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: (force?: boolean) => Promise<void>;
  refetchFlowTelemetry: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
  refetchNH3Telemetry: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
  refetchBarnTemperature: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
}

export const TelemetryContext = createContext<TelemetryContextType | undefined>(undefined);
export type { TelemetryContextType };

interface TelemetryProviderProps {
  children: ReactNode;
}

export function TelemetryProvider({ children }: TelemetryProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  // --- flowTelemetry ---
  const {
    state: flowTelemetryState,
    refetch: refetchFlowTelemetryBase,
  } = useDomainFetch<FlowTelemetryResponse[]>({
    endpoint: `${API_BASE_URL}/api/flow-telemetry`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- nh3Telemetry ---
  const {
    state: nh3TelemetryState,
    refetch: refetchNH3TelemetryBase,
  } = useDomainFetch<NH3TelemetryResponse[]>({
    endpoint: `${API_BASE_URL}/api/nh3-telemetry`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- barnTemperature ---
  const {
    state: barnTemperatureState,
    refetch: refetchBarnTemperatureBase,
  } = useDomainFetch<BarnTemperatureTelemetryResponse[]>({
    endpoint: `${API_BASE_URL}/api/barn-temperature`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- flowThresholds (configuration data -- slower refresh) ---
  // The /api/flow-thresholds endpoint wraps data in { success, data: [...] }
  // unlike other endpoints that return raw arrays.
  const {
    state: flowThresholdsState,
    refetch: refetchFlowThresholdsBase,
  } = useDomainFetch<{ success: boolean; data: FlowThreshold[] } | FlowThreshold[]>({
    endpoint: `${API_BASE_URL}/api/flow-thresholds`,
    refreshInterval: settings.refreshIntervals.status,
    getToken,
    onUnauthorized,
  });

  // Initial fetch on mount
  useEffect(() => {
    if (flowTelemetryState.isStale || !flowTelemetryState.lastFetch) {
      refetchFlowTelemetryBase();
    }
    if (nh3TelemetryState.isStale || !nh3TelemetryState.lastFetch) {
      refetchNH3TelemetryBase();
    }
    if (barnTemperatureState.isStale || !barnTemperatureState.lastFetch) {
      refetchBarnTemperatureBase();
    }
    if (flowThresholdsState.isStale || !flowThresholdsState.lastFetch) {
      refetchFlowThresholdsBase();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Re-fetch when marked stale by the auto-refresh interval
  useEffect(() => {
    if (flowTelemetryState.isStale && flowTelemetryState.lastFetch) {
      refetchFlowTelemetryBase();
    }
  }, [flowTelemetryState.isStale, flowTelemetryState.lastFetch, refetchFlowTelemetryBase]);

  useEffect(() => {
    if (nh3TelemetryState.isStale && nh3TelemetryState.lastFetch) {
      refetchNH3TelemetryBase();
    }
  }, [nh3TelemetryState.isStale, nh3TelemetryState.lastFetch, refetchNH3TelemetryBase]);

  useEffect(() => {
    if (barnTemperatureState.isStale && barnTemperatureState.lastFetch) {
      refetchBarnTemperatureBase();
    }
  }, [barnTemperatureState.isStale, barnTemperatureState.lastFetch, refetchBarnTemperatureBase]);

  useEffect(() => {
    if (flowThresholdsState.isStale && flowThresholdsState.lastFetch) {
      refetchFlowThresholdsBase();
    }
  }, [flowThresholdsState.isStale, flowThresholdsState.lastFetch, refetchFlowThresholdsBase]);

  const refetchFlowTelemetry = useCallback(
    async (force = false, queryParams?: Record<string, string>): Promise<void> => {
      await refetchFlowTelemetryBase(force, queryParams);
    },
    [refetchFlowTelemetryBase],
  );

  const refetchNH3Telemetry = useCallback(
    async (force = false, queryParams?: Record<string, string>): Promise<void> => {
      await refetchNH3TelemetryBase(force, queryParams);
    },
    [refetchNH3TelemetryBase],
  );

  const refetchBarnTemperature = useCallback(
    async (force = false, queryParams?: Record<string, string>): Promise<void> => {
      await refetchBarnTemperatureBase(force, queryParams);
    },
    [refetchBarnTemperatureBase],
  );

  const refetch = useCallback(async (force = false): Promise<void> => {
    await Promise.all([
      refetchFlowTelemetryBase(force),
      refetchNH3TelemetryBase(force),
      refetchBarnTemperatureBase(force),
      refetchFlowThresholdsBase(force),
    ]);
  }, [refetchFlowTelemetryBase, refetchNH3TelemetryBase, refetchBarnTemperatureBase, refetchFlowThresholdsBase]);

  const loading =
    flowTelemetryState.loading ||
    nh3TelemetryState.loading ||
    barnTemperatureState.loading ||
    flowThresholdsState.loading;

  const error =
    flowTelemetryState.error ||
    nh3TelemetryState.error ||
    barnTemperatureState.error ||
    flowThresholdsState.error;

  const isStale =
    flowTelemetryState.isStale ||
    nh3TelemetryState.isStale ||
    barnTemperatureState.isStale ||
    flowThresholdsState.isStale;

  const lastFetchValues = [
    flowTelemetryState.lastFetch,
    nh3TelemetryState.lastFetch,
    barnTemperatureState.lastFetch,
    flowThresholdsState.lastFetch,
  ].filter((f): f is number => f !== null);
  const lastFetch = lastFetchValues.length > 0 ? Math.min(...lastFetchValues) : null;

  // Unwrap sendSuccess() envelope from /api/flow-thresholds
  // API returns { success, data: FlowThreshold[] } but consumers expect FlowThreshold[]
  const unwrappedFlowThresholds = useMemo((): FlowThreshold[] | null => {
    const raw = flowThresholdsState.data;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    if ('data' in raw && Array.isArray(raw.data)) return raw.data;
    return null;
  }, [flowThresholdsState.data]);

  const contextValue: TelemetryContextType = {
    flowTelemetry: flowTelemetryState.data,
    nh3Telemetry: nh3TelemetryState.data,
    barnTemperature: barnTemperatureState.data,
    flowThresholds: unwrappedFlowThresholds,
    loading,
    error,
    isStale,
    lastFetch,
    refetch,
    refetchFlowTelemetry,
    refetchNH3Telemetry,
    refetchBarnTemperature,
  };

  return (
    <TelemetryContext.Provider value={contextValue}>
      {children}
    </TelemetryContext.Provider>
  );
}

export function useTelemetryContext(): TelemetryContextType {
  const context = useContext(TelemetryContext);
  if (context === undefined) {
    throw new Error('useTelemetryContext must be used within a TelemetryProvider');
  }
  return context;
}

export default TelemetryProvider;
